<template>
  <v-container
  v-if="isDataReady"
  fluid>
    <v-row v-if="$parent.isRole(['CLIENT'])" dense no-gutters>
        <v-col>
          <p class="process-repair-tab-section-subtitle text-center">{{$t('pages.repair.partsAreInWaitingApproval')}}</p>
        </v-col>
    </v-row>
    <v-row dense no-gutters class="mb-2">
        <v-col v-if="$parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SUPERVISOR'])">
          <section-title-with-request-id
          :request-id="repairProp.number"
          title="pages.repair.partsForRepairApproval"/>
        </v-col>
        <v-col v-if="$parent.isRole(['CLIENT', 'SUSTAINABILITY_MANAGER', 'SERVICE'])">
          <section-title-with-request-id
          :request-id="repairProp.number"
          title="pages.repair.partsInfo"/>
        </v-col>
        <v-col
        v-if="$parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SUPERVISOR'])"
        class="d-flex justify-end align-center"
        cols="1">
          <approval-comment-dialog-trigger
          @click="openApprovalCommentDialogForRequest"
          :value="newData.approvalComments['IN_REPAIR_APPROVAL']"
          :disable-on-empty="disabled"/>
        </v-col>
    </v-row>
    <v-row dense no-gutters>
      <v-col>
        <attachment-dialog
        v-if="attachmentDialog.model"
        v-model="attachmentDialog.model"
        :attachments.sync="attachmentDialog.attachments"
        preview/>
        <approval-comment-dialog
        v-if="approvalCommentDialog.model"
        v-model="approvalCommentDialog.model"
        :comment="approvalCommentDialog.comment"
        :status="approvalCommentDialog.status"
        :step-status="repairProp.status.key"
        :owner-id="approvalCommentDialog.ownerId"
        :saver="approvalCommentDialog.saver"
        :title="approvalCommentDialog.title"
        @success="onApprovalCommentSaved"
        :preview="approvalCommentPreviewMode"/>
        <main-data-table
        :headers="getHeaders"
        :items="newData.repairParts"
        number-pages
        :with-search="false"
        :page.sync="page"
        :size="-1"
        :items-per-page-options="[-1]"
        :total-elements="-1"
        hide-default-footer>
          <template v-slot:[`header.isApproved`]="{ }">
              <span v-if="$parent.isRole(['SERVICE'])">{{$t('headers.supervisorStatus')}}</span>
              <span v-else-if="$parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SUPERVISOR', 'CLIENT', 'SUSTAINABILITY_MANAGER'])">{{$t('headers.approvaStatus')}}</span>
          </template>
          <template
          v-slot:[`header.approvalComments`]="{}">
            {{$t(`headers.${areAllPartsRejected ? 'rejected' : 'approval'}Comment`)}}
          </template>

          <template
          v-if="$parent.isRole(['SUPERVISOR', 'SERVICE'])"
          v-slot:[`item.diagnosticStatus`]="{ item }">
            <span>
              {{item.diagnosticInfo.statusObj ? $t(item.diagnosticInfo.statusObj.value) : ''}}
            </span>
          </template>

          <template
          v-if="$parent.isNotRole(['SUPERVISOR'])"
          v-slot:[`item.attachments`]="{ item }">
            <attachments-dialog-trigger
            @click="openAttachmentsDialog(item.attachments)"
            :attachments="item.attachments"
            disable-on-empty/>
          </template>
          <template 
          v-slot:[`item.mark`]="{ item }">
              <div
              v-if="!isDiagnosticStatus(item, ['IT_IS_WORKING'])"
              class="d-flex justify-center align-center">
                <v-checkbox
                v-model="item.mark"
                :disabled="disabled"/> 
              </div>
              <div v-else></div>
          </template>
          <template v-slot:[`item.imageUrl`]="{ item }">
              <previewable-image
              v-model="item.imageUrl"
              height="65"
              width="50"
              class="py-2"/>
          </template>
          <template 
          v-if="$parent.isRole(['CLIENT', 'SUSTAINABILITY_MANAGER'])"
          v-slot:[`item.problemDescription`]="{ item }">
              <div class="py-2">
                <span>
                  {{item.problemDescription}}
                </span>  
              </div>
          </template>
          <template 
          v-if="$parent.isNotRole(['CLIENT', 'SUSTAINABILITY_MANAGER'])"
          v-slot:[`item.comment`]="{ item }">
              <div class="py-2">
                <span>
                  {{item.diagnosticInfo.comment}}
                </span>
              </div>
          </template>
          <template v-slot:[`item.diagnosticTypes`]="{ item }">
            <span>
              {{item.diagnosticInfo.pricesOrPredefinedTypes.diagnosticTypesText}}
            </span>
          </template>
          <template v-slot:[`item.repairTypes`]="{ item }">
            <span>
              {{item.diagnosticInfo.pricesOrPredefinedTypes.repairTypesText}}
            </span>
          </template>
          <template v-slot:[`item.partsTypes`]="{ item }">
            <span>
              {{item.diagnosticInfo.pricesOrPredefinedTypes.partsTypesText}}
            </span>
          </template>
          <template 
          v-if="$parent.isNotRole(['CLIENT', 'SUSTAINABILITY_MANAGER'])"
          v-slot:[`item.totalCost`]="{ item }">
              <span>
                {{item.diagnosticInfo.totalCost}}
              </span>
          </template>
          <template 
          v-slot:[`item.isApproved`]="{ item }">
            <span
            v-if="isDiagnosticStatus(item, ['IT_IS_WORKING'])"
            class="green--text"
            style="font-weight: 300;">
              {{$t('repairStatus.itIsWorking')}}
            </span>  
            <div
            v-else>
              <span v-if="isRepairApprovalStatus(item, ['APPROVED'])" class="green--text" style="font-weight: 300;">
                {{$t('initialStatuses.approved')}}
              </span>
              <span v-else-if="isRepairApprovalStatus(item, ['REJECTED'])" class="red--text" style="font-weight: 300;">
                {{$t('initialStatuses.rejected')}}
              </span>
              <span v-else-if="isRepairApprovalStatus(item, ['WAITING_APPROVAL'])" class="orange--text" style="font-weight: 300;">
                {{$t('initialStatuses.waitingApproval')}}
              </span>
            </div>
          </template>
          <template 
          v-if="$parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SUPERVISOR'])"
          v-slot:[`item.approval`]="{ item }">
              <div
              v-if="!isDiagnosticStatus(item, ['IT_IS_WORKING'])"
              class="d-flex justify-center">
                <div style="width: min-content;">
                  <primary-btn
                  @click="changeInitialStatusSingle(item, 'APPROVED')"
                  :label="$t('btns.approve')"
                  color="#4285f4"
                  class="mb-1"
                  :loading="isLoading"
                  :disabled="disabled || isRepairApprovalStatus(item, ['APPROVED'])"
                  small-text
                  :width="100"
                  :height="30"/>
                  <primary-btn
                  @click="changeInitialStatusSingle(item, 'REJECTED')"
                  :label="$t('btns.reject')"
                  color="#ff3547"
                  class="mt-1"
                  :disabled="disabled || isRepairApprovalStatus(item, ['REJECTED'])"
                  :loading="isLoading"
                  small-text
                  :width="100"
                  :height="30"/>
                </div>
              </div>
          </template>
          <template
          v-if="$parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SUPERVISOR', 'CLIENT'])"
          v-slot:[`item.approvalComments`]="{ item }">
            <approval-comment-dialog-trigger
            @click="openApprovalCommentDialogForPart(item, getApprovalCommentDialogTitleByRole(item.repairApproval))"
            :value="item.approvalComments['IN_REPAIR_APPROVAL']"
            :disable-on-empty="approvalCommentPreviewMode || isDiagnosticStatus(item, ['IT_IS_WORKING'])"/>
          </template>
        </main-data-table>
        <v-row 
          v-if="$parent.isNotRole(['CLIENT']) && withPredefinedTypes"
          dense
          class="mt-5">
            <v-col>
              <div class="d-flex justify-end">
                <total-types-multiple-rows-table :parts="newData.repairParts"/>
              </div>
            </v-col>
          </v-row>
        <v-row 
        v-if="$parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SUPERVISOR'])"
        dense class="mt-5">
          <v-col>
            <p class="text-center"
            style="font-weight: bold">
              {{$t('common.pressContinueWhenReady')}}
            </p>
            <process-repair-quick-btns
            :config="getQuickBtnsConfig"
            :loading.sync="isLoading"
            on-success-repair-status-message="dialogs.messages.success.firstDetailsCompleted"/>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <export-xlsx-row
    :width="150"
    :height="50"
    :disabled="newData.repairParts.length === 0"
    :loading.sync="isLoading"
    class="px-4 mt-4"
    name="export.title.repairApprovalTab"
    :exporter="getExporter"/>
  </v-container>
</template>
<script>
import ProcessRepairQuickBtns from '@/components/process-repair-tabs/ProcessRepairQuickBtns'
import MainDataTable from '@/components/common/MainDataTable'
import PreviewableImage from '@/components/common/PreviewableImage'
import ExportXlsxRow from '@/components/common/ExportXlsxRow'
import PrimaryBtn from '@/components/btns/PrimaryBtn'
import AttachmentDialog from '@/components/dialogs/AttachmentDialog'
import AttachmentsDialogTrigger from '@/components/common/triggers/AttachmentsDialogTrigger'
import ApprovalCommentDialog from '@/components/dialogs/ApprovalCommentDialog'
import ApprovalCommentDialogTrigger from '@/components/common/triggers/ApprovalCommentDialogTrigger'
import SectionTitleWithRequestId from '@/components/common/process-tabs/shared/SectionTitleWithRequestId'
import TotalTypesMultipleRowsTable from '@/components/common/basic-tables/TotalTypesMultipleRowsTable'
import { changePartMainStatus} from '@/repositories/repairRepo'
import { execHttpRequestThrowable } from '@/services/http'
import { getUserRole } from '@/services/authService'
import { getObjectDiagnosticStatusByKey } from '@/utils/statusesUtil'

//ENABLE_CHANGE_DIALOG - This method is needed for changes dialog.
// import { getTextByKeyInitialStatus } from '@/utils/statusesUtil'
//ENABLE_CHANGE_DIALOG - This method is needed for changes dialog.
// compareArrays
import { copyObj } from '@/utils/StringUtil'
import { exportRepairApprovalTabXlsx } from '@/repositories/exportRepo'
import { saveRequestApprovalComment, savePartApprovalComment } from "@/repositories/repairRepo"
export default {
  components: {
    ProcessRepairQuickBtns,
    MainDataTable,
    PreviewableImage,
    ExportXlsxRow,
    PrimaryBtn,
    AttachmentDialog,
    AttachmentsDialogTrigger,
    ApprovalCommentDialog,
    ApprovalCommentDialogTrigger,
    SectionTitleWithRequestId,
    TotalTypesMultipleRowsTable
  },
  props: {
    data: {
      type: Object,
      required: true
    },
    disabled: Boolean
  },
  data() {
    return {
      headers: [{
          text: 'headers.mark',
          align: 'start',
          sortable: true,
          value: 'mark',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR"]
        },{
          text: 'headers.image',
          align: 'start',
          sortable: true,
          value: 'imageUrl',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE", "CLIENT", "SUSTAINABILITY_MANAGER"]
        },{
          text: 'headers.category',
          align: 'start',
          sortable: true,
          value: 'category',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE", "CLIENT", "SUSTAINABILITY_MANAGER"]
        },{
          text: 'headers.partName',
          align: 'start',
          sortable: true,
          value: 'partName',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE", "CLIENT", "SUSTAINABILITY_MANAGER"]
        }, {
          text: 'headers.serialNumber',
          align: 'start',
          sortable: true,
          value: 'serialNumber',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE", "CLIENT", "SUSTAINABILITY_MANAGER"]
        }, {
          text: 'headers.serviceTag',
          align: 'start',
          sortable: true,
          value: 'serviceTag',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE", "CLIENT", "SUSTAINABILITY_MANAGER"]
        }, {
          text: 'headers.problemDescription',
          align: 'start',
          sortable: true,
          value: 'problemDescription',
          permissions: ["CLIENT", "SUSTAINABILITY_MANAGER"]
        }, {
          text: 'headers.diagnosticStatus',
          align: 'start',
          sortable: true,
          value: 'diagnosticStatus',
          permissions: ["SUPERVISOR", "SERVICE"]
        }, {
          text: 'headers.comment',
          align: 'start',
          sortable: true,
          value: 'comment',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE"]
        }, {
          text: 'headers.attachments',
          align: 'start',
          sortable: true,
          value: 'attachments',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SERVICE", "CLIENT", "SUSTAINABILITY_MANAGER"]
        }, 
        // NOTE: these three columns replace the /totalConst/ column on withPredefinedTypes=true
        {
          text: 'headers.diagnosticTypes',
          align: 'start',
          sortable: true,
          value: 'diagnosticTypes',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE"]
        }, {
          text: 'headers.repairTypes',
          align: 'start',
          sortable: true,
          value: 'repairTypes',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE"]
        }, {
          text: 'headers.partsUsed',
          align: 'start',
          sortable: true,
          value: 'partsTypes',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE"]
        },
        // --------------------------------
        {
          text: 'headers.totalCost',
          align: 'start',
          sortable: true,
          value: 'totalCost',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE"]
        }, {
          text: '',
          align: 'start',
          sortable: true,
          value: 'isApproved',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE", "CLIENT", "SUSTAINABILITY_MANAGER"]
        }, {
          text: 'headers.repairApproval',
          align: 'start',
          sortable: true,
          value: 'approval',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR"]
        }, {
          text: '', //NOTE: it will be processed dynamiclly.
          align: 'start',
          sortable: true,
          value: 'approvalComments',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "CLIENT"]
        }
      ],
      newData: null,
      page: 1,
      isLoading: false,
      isDataReady: false,
      attachmentDialog: {
        model: false
      },
      approvalCommentDialog: {
        model: false,
        comment: null,
        status: null,
        ownerId: -1,
        isItem: false,
        saver: null,
        title: undefined
      },
      withPredefinedTypes: false
    }
  },
  computed: {
    repairProp: {
      get: function() {
        return this.$props.data
      },
      set: function(v) {
        this.$emit('update:data', v)
      }
    },
    getHeaders() {
      const headers = []
      const hideHeadersForRequestWithPredefinedTypes = ["totalCost"]
      const hideHeadersForRequestWithPrices = ["partsTypes", "repairTypes", "diagnosticTypes"]

      for (const h of this.headers) {
        if (!h.permissions.includes(this.getRole)) continue

        if (this.withPredefinedTypes) {
          if (hideHeadersForRequestWithPredefinedTypes.includes(h.value)) continue
        }
        else {
          if (hideHeadersForRequestWithPrices.includes(h.value)) continue
        }
        
        if (this.disabled) {
          if (h.value === 'mark') continue
        }

        headers.push(h)
      }

      return headers
    },
    getRole() {
      return getUserRole()
    },
    isMarkedApprovedBtnDisable() {
      return this.newData.repairParts.filter(p => p.mark === true).length === 0
    },
    getQuickBtnsConfig() {
      return [
        {
          name: 'RejectAll',
          events: {
            'click': () => this.setChangedDialogItems('REJECTED', false)
          },
          props: {
            'disabled': this.disabled
          }
        }, {
          name: 'ApproveAll',
          events: {
            'click': () => this.setChangedDialogItems('APPROVED', false)
          },
          props: {
            'disabled': this.disabled
          }
        }, {
          name: 'ApproveMarked',
          events: {
            'click': () => this.setChangedDialogItems('APPROVED', true)
          },
          props: {
            'disabled': this.disabled || this.isMarkedApprovedBtnDisable
          }
        }, {
          name: 'Continue',
          props: {
            'disabled': this.disabled || !this.newData.isAllowedToContinue
          }
        }
      ]
    },
    getExporter() {
      return async () => await exportRepairApprovalTabXlsx(this.$route.params.id)
    },
    areAllPartsRejected() {
      const ARE_ALL_PARTS_REJECTED = this.newData.repairParts.filter(o => o.repairApproval === "APPROVED").length === 0
      return ARE_ALL_PARTS_REJECTED
    },
    approvalCommentPreviewMode() {
      return this.disabled || this.$parent.isRole(["CLIENT"])
    }
  },
  methods: {
    async changeInitialStatusSingle(part, status) {
      await this.tryToChangeRepairStatus([{
        id: part.id,
        repairApproval: status
      }], false)
    },
    async tryToChangeRepairStatus(parts, showSuccessDialog) {
      const data = parts.map(p => {
        return {
          repairPartId: p.id,
          status: p.repairApproval
        }
      })
      const status = data[0].status

      const messages = {
        ignoreSuccessDialog: !showSuccessDialog,
        _200: this.$t(status === 'APPROVED' ? 'dialogs.messages.success.approvalDecisionSaved' : 'dialogs.messages.success.rejectionDecisionSaved')
      }

      const r = await execHttpRequestThrowable(async () => await changePartMainStatus(this.$route.params.id, data),
      messages,
      v => this.isLoading = v)
      const ids = parts.map(p => p.id)
      //Update server parts
      this.repairProp.repairParts
        .filter(p => ids.includes(p.id))
        .forEach(p => p.repairApproval = String(status))
      //Update local parts
      this.newData.repairParts
        .filter(p => ids.includes(p.id))
        .forEach(p => p.repairApproval = String(status))

      this.newData.isAllowedToContinue = r.response.data
    },
    setChangedDialogItems(status, isMarkBtn) {
      const filteredParts = this.newData.repairParts.filter(p => ((isMarkBtn && p.mark && p.repairApproval !== status) || (!isMarkBtn && p.repairApproval !== status)) && !this.isDiagnosticStatus(p, ['IT_IS_WORKING']))
      if (filteredParts.length === 0) return
      this.tryToChangeRepairStatus(filteredParts.map(p => {
        return {
          id: p.id,
          repairApproval: status
        }
      }))
      // NOTE: ENABLE_CHANGE_DIALOG - Enable this code, if changes dialog is needed.
      //-----------------------------------------------------------------
      // const newParts = filteredParts.map(p => {
      //   return {
      //     rowNumber: p.rowNumber,
      //     repairApproval: status
      //   }
      // })
      // const oldParts = filteredParts.map(p => {
      //   return {
      //     rowNumber: p.rowNumber,
      //     repairApproval: p.repairApproval
      //   }
      // })
      // const props = [
      //   {
      //     field: 'headers.repairApproval',
      //     prop: 'repairApproval',
      //     getter: getTextByKeyInitialStatus
      //   }
      // ]
      // this.$changesDialog.open(
      //   'dialogs.titles.changes',
      //   compareArrays(oldParts, newParts, props),
      //   async () => await this.tryToChangeRepairStatus(filteredParts.map(p => {
      //     return {
      //       id: p.id,
      //       repairApproval: status
      //     }
      //   }), true),
      //   true
      // )
      //-----------------------------------------------------------------
    },
    openAttachmentsDialog(attachments) {
      this.attachmentDialog = {
        model: true,
        attachments
      }
    },
    openApprovalCommentDialogForPart({approvalComments, repairApproval, id}, overriedenTitle) {
      this.approvalCommentDialog = {
        model: true,
        comment: approvalComments["IN_REPAIR_APPROVAL"],
        status: repairApproval,
        ownerId: id,
        isItem: true,
        saver: savePartApprovalComment,
        title: overriedenTitle
      }
    },
    openApprovalCommentDialogForRequest() {
      this.approvalCommentDialog = {
        model: true,
        comment: this.newData.approvalComments["IN_REPAIR_APPROVAL"],
        status: "REQUEST",
        ownerId: Number(this.$route.params.id),
        isItem: false,
        saver: saveRequestApprovalComment
      }
    },
    onApprovalCommentSaved({ownerId, comment}) {
      // NOTE: for part
      if (this.approvalCommentDialog.isItem) {
        //Update server parts
        this.repairProp.repairParts
          .filter(p => p.id === ownerId)
          .forEach(p => p.approvalComments["IN_REPAIR_APPROVAL"] = String(comment))
        //Update local parts
        this.newData.repairParts
          .filter(p => p.id === ownerId)
          .forEach(p => p.approvalComments["IN_REPAIR_APPROVAL"] = String(comment))
      }
      // NOTE: for whole request
      else {
        //Update server parts
        this.repairProp.approvalComments["IN_REPAIR_APPROVAL"] = comment
        //Update local parts
        this.newData.approvalComments["IN_REPAIR_APPROVAL"] = comment
      }

      this.approvalCommentDialog.model = false
    },
    getTotalOfTypesForItem(item, types, prop) {
      let total = ""
      let dic = {}
      for (const t of types) {
        if (!(t.value in dic)) {
          dic[t.value] = {
            text: t.text,
            number: 1
          }
        }
        else {
          let n = Number(dic[t.value].number)
          dic[t.value].number = ++n
        }
      }

      const typeKeys = Object.keys(dic)
      for(let i = 0; i < typeKeys.length; i++) {
        const key = typeKeys[i]
        total = total.concat(`${dic[key].number} x ${dic[key].text}`)
        if (i + 1 < typeKeys.length) total = total.concat(" + ")
      }

      item.diagnosticInfo.pricesOrPredefinedTypes[prop] = total.length > 0 ? total : null
    },
    isDiagnosticStatus(i, s) {
      return s.includes(i.diagnosticInfo.status)
    },
    isInitialStatus(i, s) {
      return s.includes(i.initialApproval)
    },
    isRepairApprovalStatus(i, s) {
      return s.includes(i.repairApproval)
    },
    getApprovalCommentDialogTitleByRole(status) {
      if (this.$parent.isRole(["CLIENT"])) {
        switch(status) {
          case "APPROVED": return "dialogs.titles.whyWasPartApproved"
          case "REJECTED": return "dialogs.titles.whyWasPartRejected"
          default: return "dialogs.titles.partUpcomingStatusDescription"
        }
      }

      return undefined
    }
  },
  mounted() {
    this.withPredefinedTypes = this.repairProp.repairParts[0].diagnosticInfo.withPredefinedTypes
    this.newData = {
      isAllowedToContinue: this.repairProp.isAllowedToContinue,
      approvalComments: copyObj(this.repairProp.approvalComments),
      repairParts: this.repairProp.repairParts
        // .filter(rp => rp.initialApproval === 'APPROVED')
        .map((rp, i) => {
          if (this.withPredefinedTypes) {
            this.getTotalOfTypesForItem(rp, rp.diagnosticInfo.pricesOrPredefinedTypes.diagnosticTypes, "diagnosticTypesText")
            this.getTotalOfTypesForItem(rp, rp.diagnosticInfo.pricesOrPredefinedTypes.repairTypes, "repairTypesText")
            this.getTotalOfTypesForItem(rp, rp.diagnosticInfo.pricesOrPredefinedTypes.partsTypes, "partsTypesText")
          }

          const newObj = copyObj(rp)
          newObj.mark = this.$parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SUPERVISOR']) ? false : null
          newObj.rowNumber = i + 1
          newObj.diagnosticInfo.statusObj = getObjectDiagnosticStatusByKey(rp.diagnosticInfo.status)
          return newObj
        })
    }
    this.isDataReady = true
  }
}
</script>
<style>

</style>